/** Machine-owned opaque report ledger; no user-facing fields in routing metadata. */
import { z } from 'zod';
import { ContentBase64Url, EncryptedContent } from './content.js';
import { ScheduleAlert } from './device-alerts.js';
import { ScheduleDigest } from './digests.js';
import { LoadNumbers, MachineLoadFrame } from './load.js';
import { MachinePresenceFrame } from './machine-presence.js';
import { MachineProjectsFrame } from './machine-projects.js';
import { RunUsageFrame } from './run-usage.js';
import { BuildUpdateFrame, LandingUpdateFrame, MachineStatusFrame } from './wire.js';
import { HygieneReportFrame } from './work.js';
export const REPORT_RECORD_BYTES = 256_000;
export const REPORT_PLAINTEXT_BYTES = 180_000;
export const REPORT_PAGE_BYTES = 16 * REPORT_RECORD_BYTES + 4096;
export const ReportKind = z.enum(['machine', 'landing', 'build', 'hygiene']);
export const ReportMachineId = z.string().regex(/^[A-Za-z0-9:_-]{1,128}$/);
export const ReportId = z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/);
const Revision = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const Digest = ContentBase64Url.length(43);
export const ReportStream = z.strictObject({
    machineId: ReportMachineId,
    kind: ReportKind,
    id: ReportId,
});
export const ReportHead = ReportStream.extend({ revision: Revision.positive(), digest: Digest });
export const ReportContent = EncryptedContent.refine((c) => ReportKind.safeParse(c.context.kind).success &&
    ReportId.safeParse(c.context.objectId).success &&
    c.context.runId.startsWith('machine:') &&
    ReportMachineId.safeParse(c.context.runId.slice(8)).success, 'invalid report context');
export const ReportPut = z.strictObject({ content: ReportContent, previous: Digest.nullable() });
export const ReportListRequest = z.strictObject({
    machineId: ReportMachineId,
    kind: ReportKind,
    after: ReportId.optional(),
});
export const ReportListPage = z.strictObject({
    items: z.array(ReportHead).max(32),
    next: ReportId.optional(),
});
export const ReportReadRequest = ReportStream.extend({ after: Revision });
export const ReportReadPage = z.strictObject({
    head: ReportHead,
    records: z.array(ReportContent).max(16),
});
export const PrivateReportFrame = z.discriminatedUnion('type', [
    MachineStatusFrame.strict(),
    LandingUpdateFrame.strict(),
    BuildUpdateFrame.strict(),
    HygieneReportFrame.strict(),
    MachineProjectsFrame,
    RunUsageFrame,
    // ---- lock-follows-os: same-machine screen lock / idle (own stream in the machine kind) ----
    MachinePresenceFrame,
    // ---- end lock-follows-os ----
    MachineLoadFrame,
]);
export const ReportPlaintext = z.strictObject({
    version: z.literal(1),
    previous: Digest.nullable(),
    value: z.strictObject({ machineId: ReportMachineId, frame: PrivateReportFrame }),
});
export function reportKind(frame) {
    return {
        'machine.status': 'machine',
        'landing.update': 'landing',
        'build.update': 'build',
        'hygiene.report': 'hygiene',
        // Its own stream inside the machine kind (source `projects`): no new routing kind.
        'machine.projects': 'machine',
        'run.usage': 'machine',
        'machine.presence': 'machine',
        'machine.load': 'machine',
    }[frame.type];
}
/** Local mapping only: source IDs (notably build lane names) MUST NOT be routing IDs. */
export function reportSourceId(frame) {
    switch (frame.type) {
        case 'machine.status':
            return 'status';
        case 'landing.update':
            return frame.landingId;
        case 'build.update':
            return frame.buildId;
        case 'hygiene.report':
            return frame.reportId;
        case 'machine.projects':
            return 'projects';
        case 'run.usage':
            return 'usage';
        case 'machine.presence':
            return 'presence';
        case 'machine.load':
            return 'load';
    }
}
export const ReportRpcFrame = z.discriminatedUnion('method', [
    z.strictObject({
        type: z.literal('rpc'),
        id: z.string().regex(/^[A-Za-z0-9:_-]{1,128}$/),
        method: z.literal('content.report.put'),
        params: ReportPut,
    }),
    z.strictObject({
        type: z.literal('rpc'),
        id: z.string().regex(/^[A-Za-z0-9:_-]{1,128}$/),
        method: z.literal('content.report.list'),
        params: ReportListRequest,
    }),
    z.strictObject({
        type: z.literal('rpc'),
        id: z.string().regex(/^[A-Za-z0-9:_-]{1,128}$/),
        method: z.literal('content.report.read'),
        params: ReportReadRequest,
    }),
]);
/** Use before schema parsing/crypto; wire handlers must ALSO bound raw request/socket bytes. */
export function reportBytes(value, max = REPORT_RECORD_BYTES) {
    const bytes = new TextEncoder().encode(JSON.stringify(value)).byteLength;
    if (bytes > max)
        throw new Error('report too large');
    return bytes;
}
/**
 * Plaintext scheduling metadata for encrypted accounts (the only machine.status the hub keeps):
 * numbers, booleans and opaque ids. Labels, provider names and report text stay encrypted.
 */
export const ScheduleRef = z.string().regex(/^[A-Za-z0-9_-]{22}$/);
/** Hub-routed run id (already plaintext routing metadata). */
export const ScheduleRunId = z.string().regex(/^[A-Za-z0-9:_-]{1,128}$/);
/** Window codes: index into this list (no names on the wire). */
export const SCHEDULE_WINDOWS = ['5h', '7d', '7d_opus', '7d_sonnet', 'overage', 'other'];
/** Agent kind code: index into this list (the hub already routes runs by agent kind). */
export const SCHEDULE_AGENTS = ['claude', 'codex'];
const AgentCode = z
    .number()
    .int()
    .min(0)
    .max(SCHEDULE_AGENTS.length - 1);
const Pct = z.number().min(0).max(100);
const Ts = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
export const ScheduleAccount = z.strictObject({
    ref: ScheduleRef,
    agent: AgentCode,
    observedAt: Ts,
    limitReached: z.boolean(),
    limitResetsAt: Ts.optional(),
    windows: z
        .array(z.strictObject({
        window: z
            .number()
            .int()
            .min(0)
            .max(SCHEDULE_WINDOWS.length - 1),
        usedPct: Pct.optional(),
        resetsAt: Ts.optional(),
    }))
        .max(8),
});
export const MachineSchedule = z.strictObject({
    /** Which opaque account each agent kind currently bills to on this machine. */
    billing: z.array(z.strictObject({ agent: AgentCode, ref: ScheduleRef })).max(2),
    accounts: z.array(ScheduleAccount).max(32),
    /** Open "Blocked: environment" records (presence = blocked). */
    blocked: z
        .array(z.strictObject({
        ref: ScheduleRef,
        runId: ScheduleRunId,
        autoFix: z.boolean(),
        detectedAt: Ts,
    }))
        .max(64),
});
export const LoadStatusFrame = z.strictObject({
    type: z.literal('machine.status'),
    ts: Ts,
    activeRuns: z.number().int().nonnegative(),
    maxRuns: z.number().int().positive(),
    schedule: MachineSchedule.optional(),
    /** G1: numeric workstation load (numbers + one boolean); see load.ts. */
    load: LoadNumbers.optional(),
    /** g2g3-alerts: opaque active-alert markers (codes only; details stay encrypted). */
    alerts: z.array(ScheduleAlert).max(16).optional(),
    // ---- g13-digests: opaque digest-ready markers (random ref + slot code only) ----
    digests: z.array(ScheduleDigest).max(8).optional(),
    // ---- end g13-digests ----
});
//# sourceMappingURL=encrypted-reports.js.map